import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { 
  Mail, 
  MousePointer,
  Eye,
  TrendingUp,
  RefreshCw,
  FileText,
  BarChart3
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CommunicationAnalyticsTab() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("chroToken") || localStorage.getItem("authToken")}` }
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/chro-enhanced/communication-analytics`, getAuthHeaders());
      if (response.ok) {
        const result = await response.json();
        setData(result);
      }
    } catch (error) {
      console.error("Error fetching communication data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-[#C4A265]" />
      </div>
    );
  }

  const { summary, topTemplates, byRegion, trend } = data;

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[#1a3a3a]">Communication Analytics</h2>
          <p className="text-sm text-muted-foreground">Organization-wide communication effectiveness metrics</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button variant="outline" size="sm">
            <FileText className="w-4 h-4 mr-2" />
            Manage Templates
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-2">
              <Mail className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-medium text-muted-foreground">Sent This Month</span>
            </div>
            <p className="text-3xl font-bold text-blue-600">{summary.sentThisMonth}</p>
            <div className="flex items-center gap-1 mt-1">
              <TrendingUp className="w-4 h-4 text-emerald-500" />
              <span className="text-sm text-emerald-600">+{trend.change}% vs last month</span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-2">
              <Eye className="w-5 h-5 text-emerald-600" />
              <span className="text-sm font-medium text-muted-foreground">Avg Open Rate</span>
            </div>
            <p className="text-3xl font-bold text-emerald-600">{summary.avgOpenRate}%</p>
            <Progress value={summary.avgOpenRate} className="h-2 mt-2" />
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-2">
              <MousePointer className="w-5 h-5 text-purple-600" />
              <span className="text-sm font-medium text-muted-foreground">Avg Click Rate</span>
            </div>
            <p className="text-3xl font-bold text-purple-600">{summary.avgClickRate}%</p>
            <Progress value={summary.avgClickRate} className="h-2 mt-2" />
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-5 h-5 text-amber-600" />
              <span className="text-sm font-medium text-muted-foreground">Active Templates</span>
            </div>
            <p className="text-3xl font-bold text-amber-600">{summary.templatesActive}</p>
            <p className="text-sm text-muted-foreground">communication templates</p>
          </CardContent>
        </Card>
      </div>

      {/* Top Performing Templates */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-[#C4A265]" />
            Top Performing Templates
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {topTemplates.map((template, i) => (
              <div key={i} className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                <div className="w-8 h-8 bg-[#7C3AED] rounded-full flex items-center justify-center text-white font-bold text-sm">
                  {i + 1}
                </div>
                <div className="flex-1">
                  <p className="font-semibold">{template.name}</p>
                  <p className="text-xs text-muted-foreground">{template.sent} sent</p>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-center">
                    <p className="text-sm font-bold text-emerald-600">{template.openRate}%</p>
                    <p className="text-xs text-muted-foreground">Open Rate</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-bold text-purple-600">{template.clickRate}%</p>
                    <p className="text-xs text-muted-foreground">Click Rate</p>
                  </div>
                  <div className="w-32">
                    <Progress value={template.openRate} className="h-2" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Regional Effectiveness */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Communication Effectiveness by Region</CardTitle>
        </CardHeader>
        <CardContent>
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left p-3 text-sm font-semibold">Region</th>
                <th className="text-center p-3 text-sm font-semibold">Sent</th>
                <th className="text-center p-3 text-sm font-semibold">Open Rate</th>
                <th className="text-center p-3 text-sm font-semibold">Click Rate</th>
                <th className="text-center p-3 text-sm font-semibold">Effectiveness</th>
              </tr>
            </thead>
            <tbody>
              {byRegion.map((region, i) => {
                const effectiveness = Math.round((region.openRate + region.clickRate) / 2);
                
                return (
                  <tr key={i} className="border-b hover:bg-slate-50">
                    <td className="p-3">
                      <span className="mr-2">{region.flag}</span>
                      <span className="font-medium">{region.region}</span>
                    </td>
                    <td className="p-3 text-center font-semibold">{region.sent}</td>
                    <td className="p-3 text-center">
                      <Badge className={region.openRate >= 80 ? "bg-emerald-100 text-emerald-700" : region.openRate >= 70 ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-700"}>
                        {region.openRate}%
                      </Badge>
                    </td>
                    <td className="p-3 text-center">
                      <Badge className={region.clickRate >= 65 ? "bg-emerald-100 text-emerald-700" : region.clickRate >= 55 ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-700"}>
                        {region.clickRate}%
                      </Badge>
                    </td>
                    <td className="p-3 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Progress value={effectiveness} className="w-20 h-2" />
                        <span className="text-sm font-medium">{effectiveness}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
